import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock, faSparkles, faGear } from '@fortawesome/pro-regular-svg-icons'
import Typography from '@mui/material/Typography'
import {
  TableComponentV2,
  LabraToolTipV2,
  SearchBoxComponent,
  StatusChipV3,
  StatusChipBg,
  StatusChipText,
} from '@labrav/react-components'
import {
  LoadingStateComponent,
  EllipsisText,
  useChatContext,
  useLabraChat,
  GradientFaIcon,
  useTrackEvent,
} from '@labrav/common'
import { FundingRequest } from '../../types'
import {
  FundingRequestsQuery,
  FundingEligibilityProgram,
} from '../../api/fundingApi'
import { FundingEligibilityPrograms } from '../FundingEligibilityPrograms/FundingEligibilityPrograms'
import { formatAmount } from '../../utils/format'
import { statusChip } from '../../utils/statusChip'
import {
  FundingFilterState,
  SEARCH_FIELD_OPTIONS,
  appliedFilterChips,
  emptyFilterState,
  filterStateToParams,
  removeChipByKey,
  sortChip,
} from './fundingFilters'
import {
  applyFundingFiltersToParams,
  parseFundingFiltersFromUrl,
  parseFundingSortFromUrl,
} from './fundingFilterUrl'
import { FundingRequestsFilters } from './FundingRequestsFilters'
import { AppliedFiltersBar } from './AppliedFiltersBar'
import { useStyles } from './FundingRequests.styles'

type FundingTab = 'requests' | 'eligibility'

const TAB_PARAM = 'tab'
const ELIGIBILITY_TAB: FundingTab = 'eligibility'

interface FundingRequestsProps {
  requests: FundingRequest[]
  total?: number
  loading?: boolean
  hasMore?: boolean
  fetchMore?: () => void
  currency?: string
  programs?: FundingEligibilityProgram[]
  programsLoading?: boolean
  accountId?: string
  onRowClick?: (request: FundingRequest) => void
  onQueryChange?: (query: FundingRequestsQuery) => void
  onOpenSettings?: () => void
}

const MS_PER_DAY = 1000 * 60 * 60 * 24
const EXPIRY_URGENT_DAYS = 30

interface ExpiryLabel {
  text: string
  urgent: boolean
}

/**
 * Within 30 days (or expired) → urgent countdown, tinted red. Beyond that →
 * the absolute date (e.g. "Mar 28, 2025") in the default text color.
 */
const expiryLabel = (value?: string | null): ExpiryLabel | null => {
  if (!value) return null
  const expiry = new Date(value)
  if (Number.isNaN(expiry.getTime())) return null
  const days = Math.ceil((expiry.getTime() - Date.now()) / MS_PER_DAY)
  if (days < 0) return { text: 'Expired', urgent: true }
  if (days === 0) return { text: 'Expires today', urgent: true }
  if (days <= EXPIRY_URGENT_DAYS)
    return { text: `${days} day${days === 1 ? '' : 's'} left`, urgent: true }
  return {
    text: expiry.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    urgent: false,
  }
}

const currencySymbol = (currency: string) =>
  currency === 'USD' ? '$' : currency

/**
 * Column shares, keyed by the `MAPPING` field the shared table uses. Proportional
 * to what each column's widest content actually needs, measured in the browser:
 * an even split left the ids, the program chip and the Next steps summary all
 * clipping while Amount, Expiry date and Status sat half empty.
 *
 * Next steps is sized to its summary alone — the Ask-AI button floats over the
 * text rather than taking a slice of the column.
 */
const COLUMN_WIDTHS: Record<string, string> = {
  companyName: '12%',
  fundRequestId: '13%',
  opportunityId: '9.5%',
  crmOpportunityId: '11%',
  fundingProgram: '14%',
  amount: '8.5%',
  expiryDate: '10%',
  status: '9.5%',
  nextSteps: '12.5%',
}

// Header sorting covers only the columns the endpoint can sort by; the value is
// its `sort_column`. The direction toggles off the URL sort, because the table
// remounts behind the loader on every fetch and forgets its own.
const SORT_COLUMN_BY_FIELD: Record<string, string> = {
  companyName: 'company_name',
  fundingProgram: 'program',
  amount: 'amount',
  expiryDate: 'expiry',
  status: 'status',
}

const ASK_AI_TOOLTIP = 'Review with Labra AI'

const MAPPING: Record<string, string> = {
  Company: 'companyName',
  'Fund request ID': 'fundRequestId',
  'AWS Opp ID': 'opportunityId',
  'CRM Opp ID': 'crmOpportunityId',
  'Funding program': 'fundingProgram',
  Amount: 'amount',
  'Expiry date': 'expiryDate',
  Status: 'status',
  'Next steps': 'nextSteps',
}

export const FundingRequests = ({
  requests,
  total,
  loading,
  hasMore = false,
  fetchMore,
  currency = 'USD',
  programs = [],
  programsLoading,
  accountId,
  onRowClick,
  onQueryChange,
  onOpenSettings,
}: FundingRequestsProps) => {
  const { classes, cx } = useStyles()
  const symbol = currencySymbol(currency)
  const { open, startNewChat, sendPrompt } = useLabraChat()
  const track = useTrackEvent()
  // Persist the tab in the URL so a refresh (or shared link) lands on the same
  // tab instead of resetting to the requests table.
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab: FundingTab =
    searchParams.get(TAB_PARAM) === ELIGIBILITY_TAB
      ? ELIGIBILITY_TAB
      : 'requests'

  const selectTab = (tab: FundingTab) => {
    track({ component: `${tab}_tab`, action: 'clicked' })
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        if (tab === ELIGIBILITY_TAB) next.set(TAB_PARAM, tab)
        else next.delete(TAB_PARAM)
        return next
      },
      { replace: true }
    )
  }

  const askAi = (request: FundingRequest) => {
    const id = request.funding_application_id ?? request.opportunity_id
    track({ component: 'request_ask_ai', action: 'clicked' })
    open()
    startNewChat()
    sendPrompt(`Funding request ${id}: ${request.last_activity_summary}`)
  }

  const onChangeRow = (workflowId: string) => {
    const request = requests.find(r => r.workflow_id === workflowId)
    if (!request) return
    track({ component: 'request_row', action: 'clicked' })
    onRowClick?.(request)
  }

  // Filters and sort live in the URL, not local state, so the table is
  // reconstructible from its address (refresh, shared link, back button).
  const search = searchParams.toString()
  const applied = useMemo(() => parseFundingFiltersFromUrl(search), [search])
  const sort = useMemo(() => parseFundingSortFromUrl(search), [search])

  const writeSelection = (
    filterState: FundingFilterState,
    nextSort: string | undefined
  ) =>
    setSearchParams(
      prev => applyFundingFiltersToParams(prev, filterState, nextSort),
      { replace: true }
    )

  const onApplyFilters = (state: FundingFilterState) =>
    writeSelection(state, sort)

  const [searchInput, setSearchInput] = useState(applied.search)
  const [searchNow, setSearchNow] = useState(!!applied.search)
  const [showCrossMark, setShowCrossMark] = useState(!!applied.search)
  const [searchFieldLabel, setSearchFieldLabel] = useState(
    () =>
      SEARCH_FIELD_OPTIONS.find(option => option.key === applied.searchBy)
        ?.label ?? SEARCH_FIELD_OPTIONS[0].label
  )
  const searchBy =
    SEARCH_FIELD_OPTIONS.find(option => option.label === searchFieldLabel)
      ?.key ?? SEARCH_FIELD_OPTIONS[0].key

  const onSearch = (value: string) => {
    if (!value.trim()) return
    setSearchNow(true)
    setShowCrossMark(true)
    writeSelection({ ...applied, search: value, searchBy }, sort)
  }

  // The search box drops its cross mark when the text is edited or cleared after
  // a search, which un-applies that search; a new field re-runs it straight away.
  useEffect(() => {
    if (!applied.search) return
    if (!showCrossMark) writeSelection({ ...applied, search: '' }, sort)
    else if (searchBy !== applied.searchBy)
      writeSelection({ ...applied, searchBy }, sort)
  }, [showCrossMark, searchBy, applied, sort])

  const onClearFilters = () => {
    setSearchInput('')
    setSearchNow(false)
    setShowCrossMark(false)
    setSearchFieldLabel(SEARCH_FIELD_OPTIONS[0].label)
    writeSelection(emptyFilterState(), sort)
  }

  const onDeleteChip = (key: string) =>
    writeSelection(removeChipByKey(applied, key), sort)

  const onApplySort = (next?: string) => writeSelection(applied, next)

  const onClearSort = () => writeSelection(applied, undefined)

  // The URL is now the source of truth, so the fetch query is derived from it
  // rather than pushed from each handler — which also means a link opened with
  // filters already applied fetches the filtered set on first render.
  // Seeded with the unfiltered query, which is what the dashboard already starts
  // with — so an unfiltered first render emits nothing instead of triggering a
  // second, identical fetch.
  const lastEmittedQuery = useRef<string>(
    JSON.stringify({
      ...filterStateToParams(emptyFilterState()),
      sort: undefined,
    })
  )
  useEffect(() => {
    if (!onQueryChange) return
    const query = { ...filterStateToParams(applied), sort }
    const signature = JSON.stringify(query)
    // Guard identity-only changes: the dashboard refetches whenever the query
    // object changes, so re-emitting an unchanged query would double-fetch.
    if (lastEmittedQuery.current === signature) return
    lastEmittedQuery.current = signature
    onQueryChange(query)
  }, [applied, sort, onQueryChange])

  // Report what this table is showing. The filters are the panel's own model, not
  // the endpoint's params, so the agent sees what the user sees.
  useChatContext('fundingRequests', () => ({
    tab: activeTab,
    filters: applied,
    sort,
    visible: { count: total ?? requests.length, page: 1 },
  }))

  const requestedProgramNames = useMemo(
    () =>
      programs
        .filter(program => program.requests_count > 0)
        .map(program => program.program_name),
    [programs]
  )

  const chips = appliedFilterChips(applied)
  const activeSortChip = sortChip(sort)

  const objects = requests.map(request => {
    const chip = statusChip(request.display_status)
    const expiry = expiryLabel(request.expiry_date)
    const showAi = !!request.last_activity_summary
    return {
      id: request.workflow_id,
      companyName: request.company_name || '-',
      fundRequestId: request.fund_request_id,
      opportunityId: request.opportunity_id || '-',
      crmOpportunityId: request.crm_opportunity_id || '-',
      fundingProgram: (
        <LabraToolTipV2 description={request.program} tooltipPlacement="top">
          <span>
            <StatusChipV3
              bgColor={StatusChipBg.GRAY}
              textColor={StatusChipText.GRAY}
              label={request.program}
              sizeVarient="normal"
            />
          </span>
        </LabraToolTipV2>
      ),
      amount:
        request.amount == null ? (
          <Typography component="span" className={classes.muted}>
            -
          </Typography>
        ) : (
          <span className={classes.amountCell}>
            <Typography component="span" className={classes.amountSymbol}>
              {symbol}
            </Typography>
            <Typography component="span" className={classes.amountValue}>
              {formatAmount(Number(request.amount))}
            </Typography>
          </span>
        ),
      expiryDate: !expiry ? (
        <Typography component="span" className={classes.muted}>
          -
        </Typography>
      ) : expiry.urgent ? (
        <span className={cx(classes.expiryCell, classes.expiryUrgent)}>
          <FontAwesomeIcon icon={faClock} />
          <Typography component="span">{expiry.text}</Typography>
        </span>
      ) : (
        <Typography component="span" className={classes.expiryDate}>
          {expiry.text}
        </Typography>
      ),
      status: (
        <StatusChipV3
          bgColor={chip.bg}
          textColor={chip.text}
          label={chip.label}
          sizeVarient="normal"
        />
      ),
      nextSteps: (
        <span className={classes.nextCell}>
          <EllipsisText
            text={request.last_activity_summary || '-'}
            className={classes.nextText}
          />
          {showAi && (
            <span className={classes.aiBtnWrap}>
              <LabraToolTipV2
                description={ASK_AI_TOOLTIP}
                tooltipPlacement="top"
              >
                <button
                  type="button"
                  className={classes.aiBtn}
                  aria-label={ASK_AI_TOOLTIP}
                  onClick={event => {
                    event.stopPropagation()
                    askAi(request)
                  }}
                >
                  <GradientFaIcon
                    icon={faSparkles}
                    className={classes.aiIcon}
                  />
                </button>
              </LabraToolTipV2>
            </span>
          )}
        </span>
      ),
    }
  })

  return (
    <section className={classes.card} data-testid="funding-requests">
      <div className={classes.header}>
        <div className={classes.tabs}>
          <button
            type="button"
            className={cx(
              classes.tab,
              activeTab === 'requests' && classes.tabActive
            )}
            onClick={() => selectTab('requests')}
          >
            Funding requests
          </button>
          <button
            type="button"
            className={cx(
              classes.tab,
              activeTab === 'eligibility' && classes.tabActive
            )}
            onClick={() => selectTab('eligibility')}
          >
            Funding eligibility programs
          </button>
        </div>
        <div className={classes.toolbar}>
          {activeTab === 'requests' && (
            <>
              <FundingRequestsFilters
                applied={applied}
                programNames={requestedProgramNames}
                onApply={onApplyFilters}
                onClear={onClearFilters}
              />
              <span className={classes.toolbarDivider} />
            </>
          )}
          <button
            type="button"
            className={classes.toolbarBtn}
            aria-label="Settings"
            onClick={onOpenSettings}
          >
            <FontAwesomeIcon icon={faGear} />
          </button>
        </div>
      </div>
      {activeTab === 'requests' ? (
        <>
          <div className={classes.searchRow}>
            <SearchBoxComponent
              options={SEARCH_FIELD_OPTIONS.map(option => option.label)}
              handleSearchClick={onSearch}
              loading={!!loading}
              searchNow={searchNow}
              setSearchNow={setSearchNow}
              showCrossMark={showCrossMark}
              setShowCrossMark={setShowCrossMark}
              selectedOption={searchFieldLabel}
              setSelectedOption={setSearchFieldLabel}
              inputValue={searchInput}
              setInputValue={setSearchInput}
            />
          </div>
          <AppliedFiltersBar
            sort={activeSortChip}
            onClearSort={onClearSort}
            filters={chips}
            onDeleteFilter={onDeleteChip}
          />
          <div className={classes.tableFill}>
            {loading ? (
              <LoadingStateComponent
                heading="Fetching all your funding requests"
                description="It may take a few moments, please wait..."
              />
            ) : (
              <TableComponentV2
                keyName="funding-requests"
                mapping={MAPPING}
                columnWidths={COLUMN_WIDTHS}
                objects={objects}
                count={total ?? objects.length}
                showCheckbox={false}
                selectedItem={[]}
                onChangeCheckbox={() => undefined}
                onChangeRow={onRowClick ? onChangeRow : undefined}
                fetchMoreData={() => fetchMore?.()}
                hasMoreData={hasMore}
                sortableFields={Object.keys(SORT_COLUMN_BY_FIELD)}
                onSortChange={field => {
                  const column = SORT_COLUMN_BY_FIELD[field]
                  onApplySort(
                    sort === `${column}_asc`
                      ? `${column}_desc`
                      : `${column}_asc`
                  )
                }}
                tableHeight="100%"
              />
            )}
          </div>
        </>
      ) : (
        <FundingEligibilityPrograms
          programs={programs}
          loading={programsLoading}
          currency={currency}
          accountId={accountId}
        />
      )}
    </section>
  )
}
